import memberRequest from './memberRequest';
import { ApiError } from './createClient';
import { translate } from '../i18n/translate';
import { getStoredLocale } from '../constants/locale';
import { MEMBER_API } from '../constants/endpoints';
import { pickFollowGlobalSettingsFromTenantList } from '../utils/globalFollowSettings';
import { pickKycConfigFromTenantList } from '../utils/kycConfig';
import { pickPaymentSettingFromTenantList } from '../utils/paymentSettingConfig';
import { pickRegisterConfigFromTenantList } from '../utils/registerConfig';
import { pickTimeZoneConfigFromTenantList } from '../utils/timeZoneConfig';

export const memberApi = {
  login: (data) =>
    memberRequest.post(`${MEMBER_API}/memberEnt/login`, data),

  registerTourist: () =>
    memberRequest.post(`${MEMBER_API}/memberEnt/register/tourists`),

  registerEmail: (data) =>
    memberRequest.post(`${MEMBER_API}/memberEnt/registerEmail`, data),

  registerMobile: (data) =>
    memberRequest.post(`${MEMBER_API}/memberEnt/registerMobile`, data),

  // A receipt acknowledges this process only, never SMTP delivery or durable queuing.
  sendVerifyCodeEmail: async (data) => {
    const receipt = await memberRequest.post(`${MEMBER_API}/memberEnt/verify-code/email`, data);
    if (receipt?.status !== 'REQUEST_ACCEPTED') {
      throw new ApiError(translate(getStoredLocale(), 'emailVerification.receiptInvalid'), 'EMAIL_RECEIPT_INVALID');
    }
    return receipt;
  },

  sendVerifyCodePhone: (data) =>
    memberRequest.post(`${MEMBER_API}/memberEnt/verify-code/phone`, data),

  googleCallback: (data) =>
    memberRequest.post(`${MEMBER_API}/memberEnt/registerLoginGoogleCallback`, data),

  getInfo: () => memberRequest.get(`${MEMBER_API}/memberCenter/info`),

  /**
   * Partial public identity update (nickname / avatar objectKey).
   * Omitted fields stay unchanged; explicit null / blank clears.
   * Do not send memberId, role, status, or bio.
   */
  updateProfile: (data) => memberRequest.put(`${MEMBER_API}/memberCenter/profile`, data),

  /** 後補邀請碼（僅影響後續佣金歸屬） */
  backfillInviterCode: (data) =>
    memberRequest.post(`${MEMBER_API}/memberCenter/backfillInviterCode`, data),

  getWallet: () => memberRequest.get(`${MEMBER_API}/memberCenter/getWallet`),

  forgetPassword: (data) =>
    memberRequest.post(`${MEMBER_API}/memberCenter/forgetPassword`, data),

  /** 已登入重設密碼：resetType=LOGIN 登入密碼、SAFE 交易／提現密碼 */
  setPassword: (data) =>
    memberRequest.post(`${MEMBER_API}/memberCenter/setPassword`, data),

  /** 前台語系列表（status=1） */
  listLanguages: () => memberRequest.get(`${MEMBER_API}/memberCenter/languageList`),

  /** 站點參數列表（公開，來源：tenant_config 表） */
  getTenantConfig: () => memberRequest.get(`${MEMBER_API}/memberEnt/tenant-config`),

  /** 跟單全域開關（由 tenant_config.global 解析） */
  getFollowGlobalSettings: async () => {
    const list = await memberRequest.get(`${MEMBER_API}/memberEnt/tenant-config`);
    return pickFollowGlobalSettingsFromTenantList(list);
  },

  /** KYC 設定（由 tenant_config.kyc 解析） */
  getKycConfig: async () => {
    const list = await memberRequest.get(`${MEMBER_API}/memberEnt/tenant-config`);
    return pickKycConfigFromTenantList(list);
  },

  /** 充提參數（由 tenant_config.paymentSetting 解析） */
  getPaymentSettingConfig: async () => {
    const list = await memberRequest.get(`${MEMBER_API}/memberEnt/tenant-config`);
    return pickPaymentSettingFromTenantList(list);
  },

  /** 登入／註冊開關（由 tenant_config.register 解析） */
  getRegisterConfig: async () => {
    const list = await memberRequest.get(`${MEMBER_API}/memberEnt/tenant-config`);
    return pickRegisterConfigFromTenantList(list);
  },

  /** 日期／時間顯示設定（由 tenant_config.timeZone 解析） */
  getTimeZoneConfig: async () => {
    const list = await memberRequest.get(`${MEMBER_API}/memberEnt/tenant-config`);
    return pickTimeZoneConfigFromTenantList(list);
  },

  /** 國家列表（公開，來源 country 表） */
  listCountries: (params) =>
    memberRequest.get(`${MEMBER_API}/memberEnt/country-list`, { params }),

  /** 本人 KYC 認證資料 */
  getKycDetail: () => memberRequest.get(`${MEMBER_API}/memberCenter/kyc/detail`),

  /** 送審 KYC */
  submitKyc: (data) => memberRequest.post(`${MEMBER_API}/memberCenter/kyc/submit`, data),

  uploadOssImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    // Result.data = objectKey (path only; no OSS bucket host)
    return memberRequest.post(`${MEMBER_API}/common/upload/oss/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** IM 聊天圖片上傳 → objectKey 前綴 image/im/yyyyMMdd/ */
  uploadOssImImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return memberRequest.post(`${MEMBER_API}/common/upload/oss/image/im`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** Travel 聊天／產品圖片 → objectKey 前綴 travel/image/yyyyMMdd/ */
  uploadOssTravelImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return memberRequest.post(`${MEMBER_API}/common/upload/oss/travel/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** IM 聊天 GIF 上傳 → objectKey 前綴 image/gif/yyyyMMdd/（僅 .gif） */
  uploadOssImGif: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return memberRequest.post(`${MEMBER_API}/common/upload/oss/image/gif`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** IM 語音上傳 → audio/im/yyyyMMdd/ */
  uploadOssImAudio: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return memberRequest.post(`${MEMBER_API}/common/upload/oss/audio/im`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** IM 影片上傳 → video/im/yyyyMMdd/ */
  uploadOssImVideo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return memberRequest.post(`${MEMBER_API}/common/upload/oss/video/im`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** IM 檔案上傳 → file/im/yyyyMMdd/ */
  uploadOssImFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return memberRequest.post(`${MEMBER_API}/common/upload/oss/file/im`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** 首頁最新新聞（已發布且首頁顯示） */
  newsLatest: (params) =>
    memberRequest.get(`${MEMBER_API}/content/news/latest`, { params }),

  /** 新聞分頁列表 */
  newsPageQuery: (params) =>
    memberRequest.get(`${MEMBER_API}/content/news/page-query`, { params }),

  /** 新聞詳情 */
  newsDetail: (id, params) =>
    memberRequest.get(`${MEMBER_API}/content/news/${id}`, { params }),

  /** 首頁輪播圖（依語系，僅回傳已配置圖片） */
  carouselList: (params) =>
    memberRequest.get(`${MEMBER_API}/content/carousel/list`, { params }),

  /** 一般跑馬燈（依語系，逐則切換） */
  marqueeList: (params) =>
    memberRequest.get(`${MEMBER_API}/content/marquee/list`, { params }),

  /** 動態跑馬燈（依語系，水平滾動；開關關閉時為空） */
  dynamicMarqueeList: (params) =>
    memberRequest.get(`${MEMBER_API}/content/marquee/dynamic-list`, { params }),

  /** 彈窗公告（依語系，無預設回退；未發布或無內容時為 null） */
  popAnnouncement: (params) =>
    memberRequest.get(`${MEMBER_API}/content/pop`, { params }),

  /** 公告中心分頁列表（依語系，僅已發布） */
  announcementCenterPageQuery: (params) =>
    memberRequest.get(`${MEMBER_API}/content/announcement-center/page-query`, { params }),

  /** 公告中心詳情（依語系，無預設回退） */
  announcementCenterDetail: (id, params) =>
    memberRequest.get(`${MEMBER_API}/content/announcement-center/${id}`, { params }),

  /** 幫助中心列表（依語系，無預設回退） */
  helpCenterList: (params) =>
    memberRequest.get(`${MEMBER_API}/content/help-center/list`, { params }),

  /** 幫助中心詳情（依語系，無預設回退） */
  helpCenterDetail: (id, params) =>
    memberRequest.get(`${MEMBER_API}/content/help-center/${id}`, { params }),

  /** 關於我們（依語系，無預設回退） */
  aboutUs: (params) =>
    memberRequest.get(`${MEMBER_API}/content/about`, { params }),

  /** 用戶協議（依語系，無預設回退） */
  agreement: (params) =>
    memberRequest.get(`${MEMBER_API}/content/agreement`, { params }),

  /** 隱私條款（依語系，無預設回退） */
  privacyPolicy: (params) =>
    memberRequest.get(`${MEMBER_API}/content/privacy-policy`, { params }),

  /** 新手指南（依語系；開關關閉時 body 為空） */
  beginnerGuide: (params) =>
    memberRequest.get(`${MEMBER_API}/content/beginnerGuide`, { params }),

  /** 客服中心列表（依語系，僅啟用項） */
  customerServiceCenterList: (params) =>
    memberRequest.get(`${MEMBER_API}/content/customer-service-center`, { params }),

  /** 客服中心展示圖（依語系，無則回退 default） */
  customerServiceCenterImage: (params) =>
    memberRequest.get(`${MEMBER_API}/content/customer-service-center/findImageByLang`, { params }),

  /** 站內信分頁列表（需登入，依語系） */
  inboxPageQuery: (params) =>
    memberRequest.get(`${MEMBER_API}/content/inbox/page-query`, { params }),

  /** 站內信詳情（需登入，依語系；開啟時標記已讀） */
  inboxDetail: (id, params) =>
    memberRequest.get(`${MEMBER_API}/content/inbox/${id}`, { params }),

  /** 標記單則站內信已讀 */
  inboxRead: (id) =>
    memberRequest.patch(`${MEMBER_API}/content/inbox/read/${id}`),

  /** 全部站內信標記已讀 */
  inboxReadAll: () =>
    memberRequest.patch(`${MEMBER_API}/content/inbox/read-all`),
};
